/**
 * SalesAllocationEditBookingDialog — "Edit (Booking)" action for Sales Control ›
 * Sales Allocation, converted 1:1 from Access `frmCtrlSalesQrySaleAlc_EditBK_Add`
 * (subform `frmCtrlSalesQrySaleAlc_EditBK`).
 *
 * Flow (Access btnEditBooking → form open with NO selected row):
 *   1. Header combo `Booking No` (required). RowSource: SELECT DISTINCT booking_no
 *      FROM public_v_delivery_order_scs WHERE booking_no is not null …
 *   2. booking_no_AfterUpdate → load every allocation row for that booking into a
 *      grid (Access requeries the subform off a temp table).
 *   3. Each row shows read-only context (Contract No · DO · Client · Contract
 *      Quality · M. Date · From WH · To WH) and 6 editable columns
 *      (Container No · Seal No · No. bag · Tare weight · Tare carton & pallet ·
 *      Tare cont). Column order = subform Top coords.
 *   4. btnSave_Click → one UPDATE per row over public_sales_allocation_scs
 *      (only the 6 editable columns), WHERE sales_allocation_scs_id = … AND
 *      is_status = 0. MsgBox "Please choose Booking No!" if booking blank.
 *
 * Numeric Standard fields (#,##0.00): tare_weight / tare_carton_pallet / tare_cont
 * are right-aligned, format on blur. number_bags is an integer.
 *
 * BACKEND NOT YET BUILT — see saleAllocationEditBookingApi.ts. Combos/rows fall
 * back to empty; no fake data. Save is a no-op until the TODO BE endpoints land.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'

import {
  type SaleAlcEditBookingLookupItem,
  type SaleAlcEditBookingPatch,
  type SaleAlcEditBookingWrite,
  useSaleAlcEditBookingLookupBookingNosQuery,
  useLazyGetSaleAlcEditBookingRowsQuery,
  useUpdateSaleAlcEditBookingMutation,
} from '../../store/api/saleAllocationEditBookingApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { DISPLAY_DATE_FORMAT } from '../../utils/date'
import dayjs from 'dayjs'

interface Props {
  open: boolean
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Per-row editable draft (the 6 "(Edit)" columns), kept as strings for inputs. */
interface RowDraft {
  container_no: string
  seal_no: string
  number_bags: string
  tare_weight: string
  tare_carton_pallet: string
  tare_cont: string
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const headCellSx: SxProps<Theme> = { fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap', py: 0.75 }
const roCellSx: SxProps<Theme> = { fontSize: '0.78rem', whiteSpace: 'nowrap', color: 'text.secondary', py: 0.5 }
const cellInputSx: SxProps<Theme> = { '& .MuiInputBase-input': { py: 0.4, fontSize: '0.78rem' } }
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }
const intProps = { style: { textAlign: 'right' as const }, inputMode: 'numeric' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))
const fmtDate = (v: unknown): string => {
  const str = s(v)
  if (!str) return ''
  const d = dayjs(str)
  return d.isValid() ? d.format(DISPLAY_DATE_FORMAT) : str
}

export default function SalesAllocationEditBookingDialog({ open, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [bookingNo, setBookingNo] = useState('')
  const [bkQ, setBkQ] = useState('')
  // Per-row editable draft keyed by sales_allocation_scs_id.
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({})

  const bookingNos = useSaleAlcEditBookingLookupBookingNosQuery(bkQ || undefined, { skip: !open })
  const [loadRows, { data: rowsData, isFetching: rowsFetching }] = useLazyGetSaleAlcEditBookingRowsQuery()
  const [saveBooking, { isLoading: saving }] = useUpdateSaleAlcEditBookingMutation()

  const rows = useMemo(() => rowsData?.items ?? [], [rowsData])

  // Reset everything when the dialog opens (Access deletes the temp table first).
  useEffect(() => {
    if (!open) return
    setBookingNo('')
    setBkQ('')
    setDrafts({})
  }, [open])

  // booking_no_AfterUpdate → load allocation rows for the chosen booking.
  useEffect(() => {
    if (!open) return
    if (!bookingNo) { setDrafts({}); return }
    let cancelled = false
    void loadRows(bookingNo).unwrap()
      .then((res) => {
        if (cancelled) return
        const next: Record<number, RowDraft> = {}
        for (const r of res.items) {
          next[r.sales_allocation_scs_id] = {
            container_no: s(r.container_no),
            seal_no: s(r.seal_no),
            number_bags: r.number_bags == null ? '' : String(r.number_bags),
            tare_weight: fmtStd(r.tare_weight),
            tare_carton_pallet: fmtStd(r.tare_carton_pallet),
            tare_cont: fmtStd(r.tare_cont),
          }
        }
        setDrafts(next)
      })
      .catch(() => { if (!cancelled) setDrafts({}) })
    return () => { cancelled = true }
  }, [open, bookingNo, loadRows])

  const setCell = (id: number, key: keyof RowDraft, value: string) =>
    setDrafts((p) => ({ ...p, [id]: { ...p[id], [key]: value } }))
  const blurNum = (id: number, key: keyof RowDraft) =>
    setDrafts((p) => ({ ...p, [id]: { ...p[id], [key]: fmtStd(p[id]?.[key]) } }))

  // Booking No combo selection (inject current value into options like Combo helper).
  const bkOptions = bookingNos.data?.items ?? []
  const bkSel: SaleAlcEditBookingLookupItem | null =
    bookingNo ? bkOptions.find((o) => o.value === bookingNo) ?? { value: bookingNo, label: bookingNo } : null
  const bkMerged = bkSel && !bkOptions.some((o) => o.value === bkSel.value) ? [bkSel, ...bkOptions] : bkOptions

  const bookingEmpty = !bookingNo.trim()
  const canSave = canEdit && !bookingEmpty && rows.length > 0 && !rowsFetching && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canSave) return
    const patches: SaleAlcEditBookingPatch[] = rows.map((r) => {
      const d = drafts[r.sales_allocation_scs_id]
      return {
        sales_allocation_scs_id: r.sales_allocation_scs_id,
        container_no: d?.container_no.trim() || null,
        seal_no: d?.seal_no.trim() || null,
        number_bags: parseNum(d?.number_bags ?? ''),
        tare_weight: parseNum(d?.tare_weight ?? ''),
        tare_carton_pallet: parseNum(d?.tare_carton_pallet ?? ''),
        tare_cont: parseNum(d?.tare_cont ?? ''),
      }
    })
    const payload: SaleAlcEditBookingWrite = { booking_no: bookingNo.trim(), rows: patches }
    try {
      await saveBooking(payload).unwrap()
      snack.success(`Booking ${bookingNo} allocations updated`)
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Update allocations failed'))
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>Edit (Booking) — Sales Allocation</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit allocations.</Alert>}

        {/* Header — Booking No combo (Access FormHeader, required). */}
        <Box sx={{ maxWidth: 520, mb: 1.5 }}>
          <Autocomplete
            size={SM}
            options={bkMerged}
            value={bkSel}
            loading={bookingNos.isFetching}
            onChange={(_e, v) => setBookingNo(v?.value ?? '')}
            onInputChange={(_e, v, reason) => { if (reason === 'input') setBkQ(v) }}
            getOptionLabel={(o) => (o ? String(o.label ?? o.value) : '')}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField {...params} label="Booking No *" error={bookingEmpty}
                helperText={bookingEmpty ? 'Choose a Booking No to load its allocations' : undefined} />
            )}
          />
        </Box>

        {/* Detail — allocation rows for the chosen booking. Columns ordered by the
            subform Top coords: read-only context, then the 6 "(Edit)" columns. */}
        {bookingNo && (
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                // Header sticky opaque (theme MuiTableHead = gold 0.18 trong suốt →
                // scroll lòi data); gradient cream-gold qua selector thắng specificity.
                '& thead .MuiTableCell-head': {
                  backgroundColor: '#fff9f4',
                  backgroundImage: 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Contract No</TableCell>
                  <TableCell sx={headCellSx}>DO</TableCell>
                  <TableCell sx={headCellSx}>Container No.</TableCell>
                  <TableCell sx={headCellSx}>Seal No.</TableCell>
                  <TableCell sx={headCellSx} align="right">No. bag</TableCell>
                  <TableCell sx={headCellSx} align="right">Tare weight</TableCell>
                  <TableCell sx={headCellSx} align="right">Tare carton &amp; pallet</TableCell>
                  <TableCell sx={headCellSx} align="right">Tare cont</TableCell>
                  <TableCell sx={headCellSx}>Client</TableCell>
                  <TableCell sx={headCellSx}>Contract Quality</TableCell>
                  <TableCell sx={headCellSx}>M. Date</TableCell>
                  <TableCell sx={headCellSx}>From WH</TableCell>
                  <TableCell sx={headCellSx}>To WH</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsFetching && (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}
                {!rowsFetching && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No allocations found for this booking.
                    </TableCell>
                  </TableRow>
                )}
                {!rowsFetching && rows.map((r) => {
                  const id = r.sales_allocation_scs_id
                  const d = drafts[id] ?? {
                    container_no: '', seal_no: '', number_bags: '',
                    tare_weight: '', tare_carton_pallet: '', tare_cont: '',
                  }
                  return (
                    <TableRow key={id} hover>
                      <TableCell sx={roCellSx}>{s(r.sale_contract)}</TableCell>
                      <TableCell sx={roCellSx}>{s(r.delivery_order)}</TableCell>
                      <TableCell sx={{ minWidth: 130 }}>
                        <TextField fullWidth size={SM} variant="standard" sx={cellInputSx}
                          value={d.container_no} disabled={!canEdit}
                          onChange={(e) => setCell(id, 'container_no', e.target.value)} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 110 }}>
                        <TextField fullWidth size={SM} variant="standard" sx={cellInputSx}
                          value={d.seal_no} disabled={!canEdit}
                          onChange={(e) => setCell(id, 'seal_no', e.target.value)} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 90 }}>
                        <TextField fullWidth size={SM} variant="standard" sx={cellInputSx}
                          value={d.number_bags} disabled={!canEdit} inputProps={intProps}
                          onChange={(e) => setCell(id, 'number_bags', e.target.value)} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <TextField fullWidth size={SM} variant="standard" sx={cellInputSx}
                          value={d.tare_weight} disabled={!canEdit} inputProps={numProps}
                          onChange={(e) => setCell(id, 'tare_weight', e.target.value)}
                          onBlur={() => blurNum(id, 'tare_weight')} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 110 }}>
                        <TextField fullWidth size={SM} variant="standard" sx={cellInputSx}
                          value={d.tare_carton_pallet} disabled={!canEdit} inputProps={numProps}
                          onChange={(e) => setCell(id, 'tare_carton_pallet', e.target.value)}
                          onBlur={() => blurNum(id, 'tare_carton_pallet')} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <TextField fullWidth size={SM} variant="standard" sx={cellInputSx}
                          value={d.tare_cont} disabled={!canEdit} inputProps={numProps}
                          onChange={(e) => setCell(id, 'tare_cont', e.target.value)}
                          onBlur={() => blurNum(id, 'tare_cont')} />
                      </TableCell>
                      <TableCell sx={roCellSx}>{s(r.client)}</TableCell>
                      <TableCell sx={roCellSx}>{s(r.contract_quality)}</TableCell>
                      <TableCell sx={roCellSx}>{fmtDate(r.m_date)}</TableCell>
                      <TableCell sx={roCellSx}>{s(r.from_warehouse)}</TableCell>
                      <TableCell sx={roCellSx}>{s(r.to_warehouse)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  )
}
